/**
 * Utility for tracking and managing recently accessed projects per user
 */
import { apiRequest } from '@/lib/queryClient';

interface RecentProjectAccess {
  projectId: string;
  timestamp: number;
}

const MAX_RECENT_PROJECTS = 10; // Store up to 10, but display only 3

/**
 * Get the localStorage key for a specific user's recent projects
 */
function getStorageKey(userId: string): string {
  return `recentProjects_${userId}`;
}

/**
 * Track a project access for a specific user.
 * - Writes to localStorage immediately (optimistic, same-device update).
 * - Fires a non-blocking POST to the server so other devices stay in sync.
 */
export function trackProjectAccess(userId: string, projectId: string): void {
  if (!userId || !projectId) return;

  // 1. Update localStorage immediately for instant same-tab/same-device feedback
  const key = getStorageKey(userId);
  const existing = localStorage.getItem(key);
  // Guard the parse the way getRecentProjectIds() below already does. A
  // truncated or corrupt value (quota eviction, another tab mid-write) would
  // otherwise throw here and abort project navigation, with nothing to
  // self-heal it — this key is never cleared on the failure path.
  let recentProjects: RecentProjectAccess[] = [];
  if (existing) {
    try {
      const parsed = JSON.parse(existing);
      if (Array.isArray(parsed)) recentProjects = parsed;
    } catch {
      recentProjects = [];
    }
  }

  recentProjects = recentProjects.filter(p => p.projectId !== projectId);
  recentProjects.unshift({ projectId, timestamp: Date.now() });
  recentProjects = recentProjects.slice(0, MAX_RECENT_PROJECTS);

  localStorage.setItem(key, JSON.stringify(recentProjects));

  // 2. Notify other components in the same tab
  window.dispatchEvent(new CustomEvent('recentProjectsUpdated', {
    detail: { userId, projectId }
  }));

  // 3. Sync to server (non-blocking — includes CSRF token automatically)
  apiRequest('POST', '/api/users/me/recent-projects', { projectId }).catch(() => {
    // Server sync is best-effort; localStorage is the fallback
  });
}

/**
 * Get recent project IDs for a user, sorted by most recent first
 */
export function getRecentProjectIds(userId: string): string[] {
  if (!userId) return [];

  const key = getStorageKey(userId);
  const existing = localStorage.getItem(key);
  
  if (!existing) return [];

  try {
    const recentProjects: RecentProjectAccess[] = JSON.parse(existing);
    return recentProjects.map(p => p.projectId);
  } catch (error) {
    console.error('Error parsing recent projects:', error);
    return [];
  }
}

/**
 * Sort projects array by recent access, with most recent first
 * Returns a new array with recent projects first, followed by the rest
 */
export function sortProjectsByRecent<T extends { id: string }>(
  projects: T[],
  userId: string
): T[] {
  const recentIds = getRecentProjectIds(userId);
  
  if (recentIds.length === 0) {
    return projects;
  }

  // Create a map for quick lookup
  const projectMap = new Map(projects.map(p => [p.id, p]));
  
  // Get recent projects that still exist
  const recentProjects = recentIds
    .map(id => projectMap.get(id))
    .filter((p): p is T => p !== undefined);
  
  // Get remaining projects not in recent list
  const recentIdSet = new Set(recentIds);
  const otherProjects = projects.filter(p => !recentIdSet.has(p.id));
  
  return [...recentProjects, ...otherProjects];
}

/**
 * Get the top N most recent projects
 */
export function getRecentProjects<T extends { id: string }>(
  projects: T[],
  userId: string,
  limit: number = 3
): T[] {
  const sorted = sortProjectsByRecent(projects, userId);
  return sorted.slice(0, limit);
}

/**
 * Clear recent projects for a user (useful on logout)
 */
export function clearRecentProjects(userId: string): void {
  if (!userId) return;
  const key = getStorageKey(userId);
  localStorage.removeItem(key);
}
